"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { X, Coins } from "lucide-react";
import {
  signInWithGoogle,
  signInWithGithub,
  fetchSignInMethodsForEmail,
  auth,
  clearGoogleSession,
} from "../auth/firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useStoreUserMutation } from "@/services/chatApi";
import { paymentApi, useStoreCustomerMutation, useGetSubscriptionDetailsQuery } from "@/services/paymentApi";
import { setAuthUser, clearAuthUser } from "@/store/authSlice";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "@/store";

const GeometricIcon = ({ size = 24 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient id="logo-gradient" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#ac274f" />
        <stop offset="100%" stopColor="#eb638b" />
      </linearGradient>
    </defs>
    <path d="M12 2L22 12L12 22L2 12L12 2Z" fill="url(#logo-gradient)" />
  </svg>
);

export default function NavigationHeader() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");
  const [authLoading, setAuthLoading] = useState(true);
  const [warningMessage, setWarningMessage] = useState<string | null>(null);
  const dispatch = useDispatch();
  const router = useRouter();
  const [storeUser] = useStoreUserMutation();
  const [storeCustomer] = useStoreCustomerMutation();

  // 🔁 user from Redux (no persist, just in-memory)
  const user = useSelector((s: RootState) => s.auth.user);

  const { data: subscriptionData } = useGetSubscriptionDetailsQuery(user?.uid, {
    skip: !user,
  });
  
  const maxCredits = subscriptionData?.tokens?.available_credits ?? 0;
  const creditsUsed = subscriptionData?.tokens?.total_credits_used ?? 0;
  const creditsLeft = Math.round(Math.max(0, maxCredits - creditsUsed) * 10) / 10;

  // Listen to Firebase auth state and sync to Redux
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const accessToken = await firebaseUser.getIdToken(); // 🔥 get token

          const userData = {
            uid: firebaseUser.uid,
            email: firebaseUser.email || "",
            emailVerified: firebaseUser.emailVerified,
            displayName: firebaseUser.displayName || "",
            isAnonymous: firebaseUser.isAnonymous,
            photoURL: firebaseUser.photoURL || "",
            providerId: firebaseUser.providerData?.[0]?.providerId || "",
            accessToken: accessToken || "",
          };

          // 👉 store in Redux
          dispatch(setAuthUser(userData));

          // 👉 store in your backend
          try {
            await storeUser(userData).unwrap();
            console.log("✅ User info stored successfully");

            // Sync user to customer in payment billing backend
            try {
              await storeCustomer().unwrap();
              console.log("✅ Customer info synced with billing backend");
            } catch (custErr) {
              console.error("❌ Error storing customer:", custErr);
            }
          } catch (err) {
            console.error("❌ Error storing user:", err);
          }
        } catch (tokenErr) {
          console.error("❌ Failed to get ID token:", tokenErr);
        }
      } else {
        dispatch(clearAuthUser());
        dispatch(paymentApi.util.resetApiState());
      }

      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, [dispatch, storeUser, storeCustomer]);

  const handleGoogleLogin = async () => {
    try {
      await signInWithGoogle();
      setShowAuthModal(false);
      setWarningMessage(null);
      // onAuthStateChanged will handle Redux + backend
    } catch (err: any) {
      if (err.code === "auth/account-exists-with-different-credential") {
        const email = err.customData?.email;
        const methods = await fetchSignInMethodsForEmail(auth, email);
        if (methods.length > 0) {
          setWarningMessage(
            `An account already exists with this email. Please sign in using: ${methods.join(
              ", "
            )}.`
          );
        } else {
          setWarningMessage(
            `You already have an account with this email. Please login with your original provider.`
          );
        }
      } else {
        console.error("Google login failed:", err);
      }
    }
  };

  const handleGithubLogin = async () => {
    try {
      await signInWithGithub();
      setShowAuthModal(false);
      setWarningMessage(null);
      // onAuthStateChanged will handle Redux + backend
    } catch (err: any) {
      if (err.code === "auth/account-exists-with-different-credential") {
        const email = err.customData?.email;
        const methods = await fetchSignInMethodsForEmail(auth, email);
        if (methods.length > 0) {
          setWarningMessage(
            `An account already exists with this email. Please sign in using: ${methods.join(
              ", "
            )}.`
          );
        } else {
          setWarningMessage(
            `You already have an account with this email. Please login with your original provider.`
          );
        }
      } else {
        console.error("GitHub login failed:", err);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      clearGoogleSession();
      dispatch(clearAuthUser());
      dispatch(paymentApi.util.resetApiState());
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const getInitial = (name: string | null | undefined) =>
    name && name.length > 0 ? name.charAt(0).toUpperCase() : "U";

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-20 bg-transparent">
        <div className="flex w-full items-center justify-between px-6 py-4 md:py-6">
          {/* Left: Logo */}
          <div className="flex flex-1 min-w-0 items-center gap-2">
            <Link href="/" className="flex items-center gap-2 shrink-0">
              <GeometricIcon />
              <span className="text-xl font-medium text-white logo-text">
                Orignly
              </span>
            </Link>
          </div>

          {/* Right: Auth Section */}
          <div className="flex flex-1 items-center justify-end gap-4 md:gap-6">
            {authLoading ? (
              <div className="w-24 h-8 rounded-md bg-gray-800 animate-pulse" />
            ) : !user ? (
              <div className="flex items-center text-sm gap-3">
                <button
                  className="bg-accent text-foreground rounded-md px-3 py-1.5 cursor-pointer transition-all duration-300 hover:bg-white/10"
                  onClick={() => {
                    setAuthMode("login");
                    setShowAuthModal(true);
                  }}
                >
                  Log in
                </button>
                <button
                  className="bg-white text-black rounded-md px-3 py-1.5 cursor-pointer transition-all duration-300 hover:bg-primary hover:text-white"
                  onClick={() => {
                    setAuthMode("signup");
                    setShowAuthModal(true);
                  }}
                >
                  Sign up
                </button>
              </div>
            ) : (
              <div className="relative">
                {/* Right Section */}
                <div className="ml-auto flex items-center gap-4">
                  {/* Subscription */}
                  <button
                    type="button"
                    onClick={() => router.push("/pricing")}
                    className="group inline-flex items-center gap-2 rounded-full border-2 border-[#eb638b] bg-transparent px-4 py-1.5 text-sm font-semibold text-[#eb638b] transition-all hover:bg-[#eb638b] hover:text-black cursor-pointer"
                  >
                    <Coins className="h-4 w-4" />
                    <span>Credits Left</span>
                    <span className="opacity-60">|</span>
                    <span>{creditsLeft}</span>
                  </button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <div className="cursor-pointer flex items-center gap-2">
                        <Avatar className="h-7 w-7 ml-2">
                          <AvatarImage
                            src={user.photoURL || ""}
                            alt={user.displayName || "User"}
                          />
                          <AvatarFallback className="bg-muted text-sm font-medium">
                            {getInitial(user.displayName)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-white text-sm">
                          {user.displayName || user.email}
                        </span>
                      </div>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent align="end" className="w-44">
                      <DropdownMenuItem
                        onClick={() => router.push("/pricing")}
                        className="cursor-pointer text-white"
                      >
                        Credits & Plans
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={handleLogout}
                        className="cursor-pointer text-white"
                      >
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Auth Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm transition-opacity">
          <div className="bg-[#1a1a1a] rounded-2xl p-8 w-96 flex flex-col items-center gap-6 relative shadow-xl border border-gray-800 animate-fade-in">
            {/* Close Button */}
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
              onClick={() => {
                setShowAuthModal(false);
                setWarningMessage(null);
              }}
            >
              <X size={24} />
            </button>

            {/* Logo */}
            <div className="flex items-center justify-center gap-3">
              <GeometricIcon size={64} />
            </div>

            {/* Heading & Subtext */}
            {authMode === "login" ? (
              <>
                <h2 className="text-white text-2xl font-bold text-center">
                  Sign in to Orignly
                </h2>
                <p className="text-gray-400 text-sm text-center">
                  Welcome back! Please sign in to continue.
                </p>
              </>
            ) : (
              <>
                <h2 className="text-white text-2xl font-bold text-center">
                  Create your account
                </h2>
                <p className="text-gray-400 text-sm text-center">
                  Welcome! Please select an account to get started.
                </p>
              </>
            )}

            {/* Warning Message */}
            {warningMessage && (
              <div className="bg-amber-400 text-black text-sm px-4 py-2 rounded-lg w-full text-center font-medium shadow-md animate-pulse">
                {warningMessage}
              </div>
            )}

            {/* Social Buttons */}
            <button
              className="bg-gradient-to-r from-gray-100 to-gray-200 text-black font-semibold rounded-lg px-4 py-2 flex items-center justify-center gap-3 hover:from-gray-200 hover:to-gray-300 transition-all w-full shadow-md"
              onClick={handleGoogleLogin}
            >
              <img src="/google-logo.png" alt="Google" className="w-5 h-5" />
              Continue with Google
            </button>

            <button
              className="bg-gray-700 text-white font-semibold rounded-lg px-4 py-2 flex items-center justify-center gap-3 hover:bg-gray-600 hover:scale-105 transition-all w-full shadow-md"
              onClick={handleGithubLogin}
            >
              <img src="/github-logo.png" alt="GitHub" className="w-5 h-5" />
              Continue with GitHub
            </button>
          </div>
        </div>
      )}
    </>
  );
}
